import React from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '../../lib/utils';
import { NOTIF_TONES, type NotifItem } from './notificationCenterStore';

/**
 * One activity row — shared by the bell dropdown (`compact`) and the full
 * `/activity` screen (`comfortable`). Unread rows get an indigo-tint background
 * plus an indigo dot; read rows are transparent with a row hover.
 */
export function NotificationRow({
  item,
  onActivate,
  comfortable = false,
  last = false,
}: {
  item: NotifItem;
  onActivate: () => void;
  comfortable?: boolean;
  last?: boolean;
}) {
  const tone = NOTIF_TONES[item.tone];
  const Icon = item.icon;

  return (
    <button
      type="button"
      onClick={onActivate}
      className={cn(
        'group/notif relative flex w-full items-center text-left transition-colors duration-150',
        comfortable ? 'gap-3.5 px-5 py-3.5 pl-[22px]' : 'gap-3 rounded-card px-3 py-3 pl-[18px]',
        comfortable && !last && 'border-b border-hairline',
        item.unread ? 'bg-indigo-tint' : 'hover:bg-[#F7F7F8]',
      )}
    >
      {item.unread && (
        <span
          aria-hidden
          className={cn(
            'absolute size-[7px] rounded-full bg-indigo',
            comfortable ? 'left-[9px] top-[22px]' : 'left-1.5 top-[18px]',
          )}
        />
      )}

      <span
        className={cn(
          'flex shrink-0 items-center justify-center',
          comfortable ? 'size-10 rounded-[11px]' : 'size-[34px] rounded-[10px]',
          tone.bg,
          tone.color,
        )}
      >
        <Icon className={comfortable ? 'size-5' : 'size-[17px]'} />
      </span>

      <span className="min-w-0 flex-1">
        <span
          className={cn(
            'block leading-normal text-ink',
            comfortable ? 'text-[14.5px]' : 'text-[13.5px]',
            '[&_b]:font-semibold',
          )}
        >
          {item.title}
        </span>
        <span className="mt-1 flex items-center gap-2.5">
          <span className="text-[12.5px] text-ink-3">{item.time}</span>
          {!comfortable && item.action && (
            <span className="text-[12.5px] font-semibold text-indigo">{item.action} →</span>
          )}
        </span>
      </span>

      {comfortable && item.action && (
        <span
          role="button"
          tabIndex={-1}
          onClick={(e) => {
            e.stopPropagation();
            onActivate();
          }}
          className="inline-flex h-8 shrink-0 items-center rounded-pill border border-hairline px-3.5 text-[13px] font-semibold text-ink transition-colors duration-150 hover:bg-surface-2"
        >
          {item.action}
        </span>
      )}
      {comfortable && <ChevronRight className="size-[18px] shrink-0 text-ink-3" />}
    </button>
  );
}
